import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { Type } from '@angular/core';
import { normalizeTranslatedUrlPath } from './url-navigation.util';

const LOGIN_GUARD_NAME = 'EaLoginGuard';
const ORDER_PAYMENT_COMPONENT_NAME = 'OrderPaymentComponent';

export function stripQueryAndHash(url: string): string {
    const queryIndex = url.indexOf('?');
    const hashIndex = url.indexOf('#');
    let end = url.length;

    if (queryIndex !== -1) {
        end = Math.min(end, queryIndex);
    }

    if (hashIndex !== -1) {
        end = Math.min(end, hashIndex);
    }

    return url.slice(0, end);
}

export function extractPathname(url: string): string {
    const stripped = stripQueryAndHash(url);

    if (stripped.startsWith('http://') || stripped.startsWith('https://')) {
        try {
            return new URL(stripped).pathname;
        } catch {
            return stripped;
        }
    }

    return stripped.startsWith('/') ? stripped : `/${stripped}`;
}

export function buildSeoUrl(hrefLangUrl: string, canonicalBaseUrl: string): string {
    const pathname = normalizeTranslatedUrlPath(extractPathname(hrefLangUrl));
    const base = canonicalBaseUrl.replace(/\/$/, '');

    return `${base}${pathname}`;
}

export function isNoIndexRobotsContent(content: string): boolean {
    return content.toLowerCase().includes('noindex');
}

export function walkRouteSnapshot(
    snapshot: ActivatedRouteSnapshot,
    predicate: (route: ActivatedRouteSnapshot) => boolean
): boolean {
    if (predicate(snapshot)) {
        return true;
    }

    return snapshot.children.some(child => walkRouteSnapshot(child, predicate));
}

function hasLoginGuard(route: ActivatedRouteSnapshot): boolean {
    const guards = (route.routeConfig?.canActivate || []) as Type<CanActivate>[];

    return guards.some(guard => guard?.name === LOGIN_GUARD_NAME);
}

function isOrderPaymentRoute(route: ActivatedRouteSnapshot): boolean {
    const targetComponent = route.data?.targetComponent as { name?: string } | undefined;

    return targetComponent?.name === ORDER_PAYMENT_COMPONENT_NAME;
}

export function shouldSkipCanonical(
    routerUrl: string,
    routeSnapshot: ActivatedRouteSnapshot
): boolean {
    const pathname = extractPathname(routerUrl);

    if (pathname.startsWith('/auth') || pathname.startsWith('/auth-redirect')) {
        return true;
    }

    return walkRouteSnapshot(
        routeSnapshot,
        route => hasLoginGuard(route) || isOrderPaymentRoute(route)
    );
}
